import { FC, useContext, useRef } from "react";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { AdequacyAggregateGranularity, AdequacyAggregateType } from "@app/exports/enumerations";
import ExportService from "@app/exports/exportService";
import { ExportAdequacyAggregatesModel } from "@app/exports/exportModels";
import { GenerateSection } from "@app/report-customization/build-reports/components/GenerateSection";
import { ColumnConfigurationSection } from "@app/report-customization/build-reports/components/ColumnConfigurationSection";
import { ServiceAreaSection } from "@app/report-customization/build-reports/components/ServiceAreaSection";
import { FilterType } from "@app/projects/enumerators";
import { SpecialtySection } from "@app/report-customization/build-reports/components/SpecialtySection";
import { handleToastSuccess, handleToastError, reportTypeToColumnsEndpointMap } from "@app/report-customization/build-reports/helpers/helpers";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

export const SpecialtySummaryReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const exportService = useRef(new ExportService()).current;
  const { primaryId, secondaryId } = rcContext.getSelectedRunConfigurationIdsInChronologicalOrder();

  const getExportModel = () => {
    const specialtyAggregateModel: ExportAdequacyAggregatesModel = {
      // if a state or county filter is applied, it's county granularity
      aggregateGranularity: (!rcContext.routeState.filters || rcContext.routeState.filters.length === 0 || rcContext.routeState.filters.find((f) => f.filterType === FilterType.County || f.filterType === FilterType.State)) ? AdequacyAggregateGranularity.County : AdequacyAggregateGranularity.State,
      aggregateType: AdequacyAggregateType.SpecialtyGroup,
      comparedProjectRunConfigurationId: secondaryId,
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      networkAssessmentAnalysisTypes: rcContext.state.networkAssessmentAnalysisTypes,
      projectRunConfigurationId: primaryId,
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: rcContext.state.selectedCounties.map((sa) => parseInt(sa)),
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      runFrequency: rcContext.state.runFrequency,
      sharedUsers: rcContext.state.sharedUsers,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
    };

    return specialtyAggregateModel;
  }

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { isGenerating: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    exportService.postExportAdequacyAggregates(getExportModel())
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { isGenerating: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating });
      });
  }

  return (
    <>
      <div className="container ms-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <ColumnConfigurationSection
          columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.AdequacySpecialtySummary)}
          exportModel={getExportModel()}
          presets={rcContext.state.presets}
          selectedPreset={rcContext.state.selectedPreset}
          reportType={rcContext.getReportType()}
          onPresetChange={rcContext.changeSelectedPreset}
          onPresetsRefresh={rcContext.changeLoadedPresets}
        />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}